import { useState } from "react";
import styled from "@emotion/styled";
// Value import via the subpath, not the package root: the root barrel re-exports the drizzle
// schema and drags `postgres` into the browser bundle. Types alone are erased at build time and
// can come from the root, but SLOT_STATUS_INFO is real code.
import { SLOT_STATUS_INFO } from "@clustec/common/slot-status";
import type { SlotStatus } from "@clustec/common";
import { theme } from "../lib/theme";
import { media } from "../lib/breakpoints";

/**
 * A slot status with its explainer one tap away.
 *
 * The status names are Aztec's, and two of them actively mislead: `checkpoint-valid` reads as
 * success but is a miss, and `checkpoint-unvalidated` looks like a failure but carries no
 * verdict. Anyone reading this page needs that context as much as an API consumer does, so the
 * text comes from SLOT_STATUS_INFO — the same constant that generates the endpoint's documented
 * table — rather than being restated here where it could drift.
 *
 * Click rather than hover, because hover doesn't exist on a phone and this is the one place the
 * page explains its own vocabulary.
 */

/**
 * A slot's bucket for anything that colours or stacks slots. Wider than `SlotStatus` by one case:
 * `orphaned` is not a status the sentinel reports but a fact about what the chain later did with
 * the slot, and it has to be colourable alongside the statuses without pretending to be one.
 */
export type SlotBucket = SlotStatus | "orphaned";

/**
 * Statuses collapse onto a smaller palette, matching EpochTimeline: success for the only
 * definitive win, warning for "attested but never landed", muted for no-verdict, danger for
 * outright proposer failure — and the orphan slate for work that was mined and then discarded.
 */
export function slotStatusColor(status: SlotBucket): string {
  switch (status) {
    // Not a status: a slot whose checkpoint reached L1 and was then pruned away. It gets its own
    // colour because every other one would be a lie — success says the epoch was fine, danger
    // blames a proposer that did its job.
    case "orphaned":
      return theme.colors.orphaned;
    case "checkpoint-mined":
      return theme.colors.success;
    case "checkpoint-valid":
      return theme.colors.warning;
    case "checkpoint-unvalidated":
      return `${theme.colors.warning}66`;
    case "checkpoint-invalid":
    case "checkpoint-missed":
    case "blocks-missed":
      return theme.colors.danger;
    case "unknown":
      return `${theme.colors.textMuted}66`;
  }
}

/**
 * Stacking order for anything that stacks statuses, bottom-up: success first, then escalating
 * failure, so a mined base sits on the floor and failures pile visibly on top of it.
 *
 * Lives beside `slotStatusColor` because the two are one convention — a colour and a position. It
 * was previously a private array inside the dashboard's epoch strip, which meant a second chart
 * stacking the same statuses had to either import from a sibling chart or restate the order and
 * drift from it.
 */
export const STATUS_STACK_ORDER: SlotBucket[] = [
  "checkpoint-mined",
  // Directly above the mined base, because that is what it is: slots that were mined and then
  // taken back. Stacked there, a pruned epoch reads as the green floor being eaten into rather
  // than as a separate kind of failure piled on top.
  "orphaned",
  "checkpoint-unvalidated",
  "unknown",
  "checkpoint-valid",
  "checkpoint-invalid",
  "checkpoint-missed",
  "blocks-missed",
];

/**
 * Status counts in stacking order, dropping the zeroes. Any status not in the order — a new
 * taxonomy case — still draws: it lands last, above what we know about, rather than vanishing.
 */
export function orderedStatusCounts(statusCounts: Record<string, number>): [SlotBucket, number][] {
  const known = new Set<string>(STATUS_STACK_ORDER);
  const extras = Object.keys(statusCounts).filter((s) => !known.has(s)) as SlotBucket[];
  return [...STATUS_STACK_ORDER, ...extras]
    .map((status) => [status, statusCounts[status] ?? 0] as [SlotBucket, number])
    .filter(([, count]) => count > 0);
}

const Wrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  min-width: 0;
`;

const Dot = styled.span<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${(p) => p.color};
  flex-shrink: 0;
`;

const Name = styled.span`
  font-family: monospace;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text};
  white-space: nowrap;
`;

const HelpButton = styled.button`
  background: none;
  border: 1px solid ${theme.colors.border};
  border-radius: 50%;
  width: 15px;
  height: 15px;
  line-height: 1;
  padding: 0;
  font-size: 10px;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: ${theme.colors.text};
    border-color: ${theme.colors.textMuted};
  }
`;

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  width: 280px;
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  padding: ${theme.spacing.sm};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text};
  line-height: 1.5;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);

  /* Anchored right on narrow screens so a label near the edge doesn't push the popover
     outside the card — the page itself must never scroll sideways. */
  ${media.sm} {
    width: min(280px, calc(100vw - 48px));
    left: auto;
    right: 0;
  }
`;

const PopoverMeta = styled.div`
  color: ${theme.colors.textMuted};
  margin-top: ${theme.spacing.xs};
`;

const PopoverNote = styled.div`
  margin-top: ${theme.spacing.xs};
  padding-top: ${theme.spacing.xs};
  border-top: 1px solid ${theme.colors.border};
  color: ${theme.colors.textMuted};
`;

export interface SlotStatusLabelProps {
  status: SlotStatus;
  /** Hide the "?" where the same status repeats down a column. */
  showHelp?: boolean;
}

export function SlotStatusLabel({ status, showHelp = true }: SlotStatusLabelProps) {
  const [open, setOpen] = useState(false);
  const info = SLOT_STATUS_INFO[status];

  return (
    <Wrapper>
      <Dot color={slotStatusColor(status)} />
      <Name>{status}</Name>
      {showHelp && info && (
        <HelpButton
          onClick={() => setOpen((v) => !v)}
          aria-label={`What does ${status} mean?`}
          aria-expanded={open}
        >
          ?
        </HelpButton>
      )}
      {open && info && (
        <Popover onClick={() => setOpen(false)}>
          {info.description}
          <PopoverMeta>
            {info.case != null ? `Aztec case ${info.case}` : "Clustec addition"} ·{" "}
            {info.countsAsMissed ? "counts as missed" : "not counted as missed"}
          </PopoverMeta>
          {info.note && <PopoverNote>{info.note}</PopoverNote>}
        </Popover>
      )}
    </Wrapper>
  );
}
